import get from 'lodash/get';
import reject from 'lodash/reject';
import some from 'lodash/some';
import isArray from 'lodash/isArray';
import store from '@Store';

import allowedTags from '@Services/AllowedTag';

import { updateDialog, closeDialog } from '@Containers/Dialogs/DialogsActions';
import { keyify } from '@Hooks/LibraryInitialization/MoreUnderscore';

import htmlStripsService from '@Services/HtmlStripsService';
import SuggestModalCache from '@Services/SuggestModalCache';
import * as Suggestions from '@Services/Suggestions';

import {
  currentDB as currentDBSelector,
  fullUpdate as fullUpdateSelector,
  isNextPageLoading as isNextPageLoadingSelector,
  opModel as opModelSelector,
  expandedValue as expandedValueSelector,
  suggestConfig as suggestConfigSelector,
  maxPage as maxPageSelector,
} from './SuggestModalSelectors';

import {
  suggestModalSetIsLoadingReducerType,
  suggestModalSetIsNextPageLoadingReducerType,
  typeOfSuggestModal,
  suggestModalSetOpModelReducerType,
  suggestModalSetCurrentDBReducerType,
  suggestModalSetNoDataReducerType,
  suggestModalSetHighlightedItemReducerType,
  suggestModalSetCurrentPageReducerType,
  suggestModalSetExpandedValueReducerType,
  suggestModalSetFullUpdateReducerType,
  suggestModalSetSuggestConfigReducerType,
  ITEMS_PER_PAGE,
  BUFFER_SIZE,
} from './SuggestModalConstants';

import { initialState } from './SuggestModalReducer';

export const openSuggestModal = (args) => {
  return (dispatch) => {
    dispatch(
      updateDialog({
        id: typeOfSuggestModal,
        dialogType: typeOfSuggestModal,
        movable: false,
        backdrop: false,
        ...args,
      })
    );
  };
};

export const closeSuggestModal = () => {
  return (dispatch) => {
    dispatch(
      closeDialog({
        id: typeOfSuggestModal,
      })
    );
  };
};

export const dispatchOpenSuggestModal = (...args) => {
  store.dispatch(openSuggestModal(...args));
};

export const setIsLoading = (isLoading) => {
  return {
    type: suggestModalSetIsLoadingReducerType,
    payload: { isLoading },
  };
};

export const setIsNextPageLoading = (isNextPageLoading) => {
  return {
    type: suggestModalSetIsNextPageLoadingReducerType,
    payload: { isNextPageLoading },
  };
};

export const setOpModel = (opModel) => {
  return {
    type: suggestModalSetOpModelReducerType,
    payload: { opModel },
  };
};

export const setCurrentDB = (currentDB) => {
  return {
    type: suggestModalSetCurrentDBReducerType,
    payload: { currentDB },
  };
};

export const setNoData = (noData) => {
  return {
    type: suggestModalSetNoDataReducerType,
    payload: { noData },
  };
};

export const setHighlightedItem = (highlightedItem) => {
  return {
    type: suggestModalSetHighlightedItemReducerType,
    payload: { highlightedItem },
  };
};

export const setCurrentPage = (currentPage) => {
  return {
    type: suggestModalSetCurrentPageReducerType,
    payload: { currentPage },
  };
};

export const setExpandedValue = (expandedValue) => {
  return {
    type: suggestModalSetExpandedValueReducerType,
    payload: { expandedValue },
  };
};

export const setFullUpdate = (fullUpdate) => {
  return {
    type: suggestModalSetFullUpdateReducerType,
    payload: { fullUpdate },
  };
};

export const setSuggestConfig = (suggestConfig) => {
  return {
    type: suggestModalSetSuggestConfigReducerType,
    payload: { suggestConfig },
  };
};

export const resetState = () => {
  return (dispatch) => {
    dispatch(setIsLoading(initialState.get('isLoading')));
    dispatch(setOpModel(initialState.get('opModel')));
    dispatch(setNoData(initialState.get('noData')));
    dispatch(setCurrentDB(initialState.get('currentDB')));
    dispatch(setHighlightedItem(initialState.get('highlightedItem')));
    dispatch(setCurrentPage(initialState.get('currentPage')));
    dispatch(setExpandedValue(initialState.get('expandedValue')));
    dispatch(setFullUpdate(initialState.get('fullUpdate')));
    dispatch(setSuggestConfig(initialState.get('suggestConfig')));
  };
};

export const handleOpModelGroups = (groups) => {
  groups.forEach((group) => {
    // eslint-disable-next-line no-param-reassign
    group.title = allowedTags(group.title);
    // eslint-disable-next-line no-param-reassign
    group.title = htmlStripsService.getTitleWithoutHtml(group.title, 'stripexceptions');
    // eslint-disable-next-line no-param-reassign
    group.displayTitle = htmlStripsService.getTitleWithoutHtml(group.title, 'escapeall');
    // eslint-disable-next-line no-param-reassign
    group.isSelected = false;
  });
};

export const handleOpModelGroupsSelection = (opModel) => {
  const groups = get(opModel, 'groups', []);
  const selection = get(opModel, 'selection', []);

  groups.forEach((group) => {
    // eslint-disable-next-line no-param-reassign
    group.isSelected = some(selection, (selected) => selected.title === group.title);
  });
};

export const clearSelected = () => {
  return (dispatch, getState) => {
    const opModel = opModelSelector(getState());
    // eslint-disable-next-line no-param-reassign,no-return-assign
    opModel.groups.forEach((item) => (item.isSelected = false));

    const updatedOpModel = {
      ...opModel,
      selection: [],
    };
    dispatch(setOpModel(updatedOpModel));
  };
};

export const getUpdatedGroups = (groups, item) => {
  const targetItem = groups.find((group) => group.title === item.title);
  targetItem.isSelected = item.isSelected;
  return groups;
};

export const pushToSelected = (item) => {
  return (dispatch, getState) => {
    const opModel = opModelSelector(getState());

    if (item.isSelected) {
      const updatedGroups = getUpdatedGroups(opModel.groups, item);

      dispatch(
        setOpModel({
          ...opModel,
          groups: updatedGroups,
          selection: [...opModel.selection, item],
        })
      );
    } else {
      const updatedGroups = getUpdatedGroups(opModel.groups, item);
      const updatedSelection = reject(opModel.selection, (selected) => item.title === selected.title);

      dispatch(
        setOpModel({
          ...opModel,
          groups: updatedGroups,
          selection: updatedSelection,
        })
      );
    }
  };
};

export const applyChanges = (closeCallback) => {
  return (dispatch, getState) => {
    const opModel = opModelSelector(getState());

    if (opModel.title === 'Chemical Name') {
      // eslint-disable-next-line no-param-reassign,no-return-assign
      opModel.selection.forEach((value) => (value.title = `"${value.title}"`));
      dispatch(setOpModel(opModel));
    }

    dispatch(closeSuggestModal());
    if (closeCallback) {
      closeCallback({ selected: opModel.selection });
    }
  };
};

export const resolveCacheOptions = () => {
  return (dispatch, getState) => {
    const currentDB = currentDBSelector(getState());
    const suggestConfig = suggestConfigSelector(getState());

    const { fieldIds, databaseId } = suggestConfig;
    const updatedFieldIds = isArray(fieldIds) ? fieldIds : [fieldIds];
    const updatedDatabaseId = currentDB || databaseId || 0;

    const key = keyify({
      databaseId: updatedDatabaseId,
      fieldIds: updatedFieldIds.join(','),
    });

    return {
      key,
      fieldIds: updatedFieldIds,
      databaseId: updatedDatabaseId,
    };
  };
};

export const updateOpModel = (loadConfig, data) => {
  return (dispatch, getState) => {
    const { firstItem, matchesCount, isPreviousPageLoad } = loadConfig;
    const fullUpdate = fullUpdateSelector(getState());
    const opModel = opModelSelector(getState());

    let first;
    let last;

    if (!fullUpdate) {
      if (isPreviousPageLoad) {
        first = opModel.firstItem - matchesCount;
        last = opModel.lastItem;
      }

      if (!isPreviousPageLoad) {
        first = opModel.firstItem;
        last = firstItem + matchesCount;
      }
    }

    if (fullUpdate) {
      first = firstItem;
      last = firstItem + matchesCount;
    }

    const groups = isPreviousPageLoad ? [...data.sub, ...opModel.groups] : [...opModel.groups, ...data.sub];
    const updatedOpModel = {
      ...opModel,
      firstItem: first,
      lastItem: last,
      groups: !fullUpdate ? groups : data.sub,
      groupCount: data.subCount,
    };

    handleOpModelGroupsSelection(updatedOpModel);
    dispatch(setOpModel(updatedOpModel));
  };
};

export const loadCachedData = (cachedOptions, loadConfig) => {
  return (dispatch) => {
    const { key } = cachedOptions;
    const { firstItem, matchesCount } = loadConfig;

    const cached = SuggestModalCache.getCached(key, firstItem, matchesCount);

    if (!cached) {
      return null;
    }

    dispatch(updateOpModel(loadConfig, cached));
    return cached.sub;
  };
};

export const loadNotCachedData = (cachedOptions, loadConfig) => {
  return async (dispatch) => {
    const { databaseId, fieldIds, key } = cachedOptions;
    const { firstItem, matchesCount } = loadConfig;
    const data = await Suggestions.getQletLookup({
      databaseId,
      matchesCount,
      fieldIds,
      firstItem,
      origin: 'LOOKUP_MODAL',
    });

    if (!data || !data.subCount) {
      return Promise.reject();
    }

    handleOpModelGroups(data.sub);

    SuggestModalCache.saveCached(key, data.firstItem, data.sub, data.subCount);
    dispatch(updateOpModel(loadConfig, data));
    return data.sub;
  };
};

export const loadData = (loadConfig) => {
  return async (dispatch) => {
    dispatch(setIsNextPageLoading(true));
    const cachedOptions = dispatch(resolveCacheOptions());
    dispatch(setNoData(false));

    let result;
    try {
      result = dispatch(loadCachedData(cachedOptions, loadConfig));

      if (!result) {
        await dispatch(loadNotCachedData(cachedOptions, loadConfig));
      }
    } catch (e) {
      Logger.reject(e);
      dispatch(clearSelected());
      dispatch(setNoData(true));
    } finally {
      dispatch(setIsNextPageLoading(false));
      dispatch(setIsLoading(false));
    }
  };
};

export const loadHighlightedData = () => {
  return async (dispatch, getState) => {
    const { databaseId, fieldIds } = dispatch(resolveCacheOptions());
    const expandedValue = expandedValueSelector(getState());

    const data = await Suggestions.getQletLookup({
      databaseId,
      matchesCount: 1,
      fieldIds,
      expandedValue,
      origin: 'LOOKUP_MODAL',
    });

    if (!data.highlightedItem) {
      return null;
    }

    return { highlightedPage: Math.ceil(data.highlightedItem / ITEMS_PER_PAGE), highlightedItem: data.highlightedItem };
  };
};

export const loadNexPage = (pageNumber, loadConfig, skipLoad) => {
  return async (dispatch, getState) => {
    dispatch(setCurrentPage(pageNumber));
    const isNextPageLoading = isNextPageLoadingSelector(getState());
    if (isNextPageLoading) {
      return;
    }
    dispatch(setFullUpdate(false));
    if (skipLoad) {
      return;
    }
    await dispatch(loadData(loadConfig));
  };
};

export const selectPage = (pageNumber) => {
  return async (dispatch, getState) => {
    const maxPage = maxPageSelector(getState());
    const opModel = opModelSelector(getState());
    let firstItem = (pageNumber - 1) * ITEMS_PER_PAGE + 1;

    if (pageNumber === maxPage && maxPage !== 1) {
      const targetPage = opModel.groupCount / ITEMS_PER_PAGE;
      firstItem = (targetPage - 1) * ITEMS_PER_PAGE + 1;
    }

    dispatch(setIsLoading(true));
    dispatch(setCurrentPage(pageNumber));
    dispatch(setFullUpdate(true));

    const loadConfig = {
      firstItem,
      matchesCount: BUFFER_SIZE,
    };
    await dispatch(loadData(loadConfig));
  };
};

export const selectDatabase = (database) => {
  return (dispatch, getState) => {
    const currentDB = currentDBSelector(getState());
    if (database !== currentDB) {
      dispatch(setCurrentDB(database));
      dispatch(setExpandedValue(''));
      dispatch(selectPage(1));
    }
  };
};

export const applySearch = (expandedValue) => {
  return async (dispatch) => {
    dispatch(setExpandedValue(expandedValue));
    dispatch(setIsLoading(true));
    dispatch(setHighlightedItem(null));

    const highlightedData = await dispatch(loadHighlightedData());

    if (!highlightedData) {
      dispatch(setIsLoading(false));
      return;
    }
    const { highlightedPage, highlightedItem } = highlightedData;

    await dispatch(selectPage(highlightedPage));
    dispatch(setHighlightedItem(highlightedItem));
  };
};

export const searchByKeyword = (keyword) => {
  return async (dispatch) => {
    if (keyword) {
      await dispatch(applySearch(keyword));
    } else {
      dispatch(setExpandedValue(''));
      dispatch(selectPage(1));
      dispatch(setHighlightedItem(null));
    }
  };
};

export const init = (ownProps) => {
  const { suggestConfig, suggestInit } = ownProps;
  return async (dispatch) => {
    dispatch(resetState());
    dispatch(setSuggestConfig(suggestConfig));
    dispatch(setIsLoading(true));
    dispatch(setCurrentDB(suggestConfig.databaseId));
    dispatch(
      setOpModel({
        title: suggestConfig.title,
        selection: [],
        groups: [],
      })
    );

    const loadConfig = {
      firstItem: 1,
      matchesCount: BUFFER_SIZE,
    };

    // Apply initial search
    if (suggestInit.search) {
      return dispatch(applySearch(suggestInit.search));
    }

    return dispatch(loadData(loadConfig));
  };
};
